
import { supabase } from '@/integrations/supabase/client';
import { getCandidateAssessments } from './admin/candidateService';
import type { FilterState } from '@/types/filterTypes';

export interface AdminCounts {
  behavioral: number;
  idealProfiles: number;
}

export interface PaginatedData<T> {
  data: T[];
  count: number;
}

export class AdminService {
  static async getCurrentUserRole(activeCompanyId?: string): Promise<{ isSuperAdmin: boolean; companyId?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { isSuperAdmin: false };

      // Check if user is super admin using the new RLS-safe function
      const { data: superAdminCheck } = await supabase.rpc('is_super_admin', { 
        _user_id: user.id 
      });

      if (superAdminCheck) {
        return { isSuperAdmin: true };
      }

      // Use the active company from context if provided, otherwise fallback to user's default company
      const companyId = activeCompanyId || await this.getUserDefaultCompanyId(user.id);

      return { 
        isSuperAdmin: false, 
        companyId: companyId 
      };
    } catch (error) {
      console.error('Error checking user role:', error);
      return { isSuperAdmin: false };
    }
  }

  private static async getUserDefaultCompanyId(userId: string): Promise<string | undefined> {
    try {
      const { data: companyId } = await supabase.rpc('get_user_company_id', {
        _user_id: userId
      });
      return companyId;
    } catch (error) {
      console.error('Error getting user default company:', error);
      return undefined;
    }
  }

  static async getTotalCounts(activeCompanyId?: string): Promise<AdminCounts> {
    console.log('AdminService.getTotalCounts: Using company ID:', activeCompanyId);
    const { isSuperAdmin, companyId } = await this.getCurrentUserRole(activeCompanyId);

    let behavioralQuery = supabase.from('survey_submissions').select('*', { count: 'exact', head: true }).neq('assessment_type', 'work_behavior').is('deleted_at', null);
    let profilesQuery = supabase.from('ideal_profiles').select('*', { count: 'exact', head: true });

    // Apply company filtering only for non-super admins
    if (!isSuperAdmin && companyId) {
      behavioralQuery = behavioralQuery.eq('company_id', companyId);
      profilesQuery = profilesQuery.eq('company_id', companyId);
    }

    const [behavioralResult, profilesResult] = await Promise.all([
      behavioralQuery,
      profilesQuery
    ]);

    if (behavioralResult.error) {
      console.error('Error fetching behavioral count:', behavioralResult.error);
      throw behavioralResult.error;
    }
    if (profilesResult.error) {
      console.error('Error fetching profiles count:', profilesResult.error);
      throw profilesResult.error;
    }

    return {
      behavioral: behavioralResult.count || 0,
      idealProfiles: profilesResult.count || 0
    };
  }

  static async getBehavioralSubmissions(
    page: number,
    pageSize: number,
    searchTerm?: string,
    filters?: FilterState,
    activeCompanyId?: string
  ): Promise<PaginatedData<any>> {
    console.log('AdminService.getBehavioralSubmissions: Using company ID:', activeCompanyId);
    const { isSuperAdmin, companyId } = await this.getCurrentUserRole(activeCompanyId);

    // Handle position filtering - need to fetch submission IDs first
    let positionSubmissionIds: string[] | null = null;
    let isUnassignedFilter = false;
    
    if (filters?.position && filters.position !== 'all') {
      if (filters.position === 'unassigned') {
        isUnassignedFilter = true;
        // Fetch all submission IDs that ARE assigned to any position
        const { data: assignedMembers } = await supabase
          .from('position_members')
          .select('submission_id');
        positionSubmissionIds = assignedMembers?.map(m => m.submission_id) || [];
      } else {
        // Fetch submission IDs for the specific position
        const { data: positionMembers } = await supabase
          .from('position_members')
          .select('submission_id')
          .eq('position_id', filters.position);
        positionSubmissionIds = positionMembers?.map(m => m.submission_id) || [];
      }
    }

    // Optimized column selection - includes cached_insights for AI insights caching
    const startQuery = supabase
      .from('survey_submissions')
      .select(`
        id, full_name, email, profile_name, completed_at, created_at, status, company_id, assessment_type, assessment_metadata, assessment_variant, scores, selected_words, selected_words_text, cached_insights
      `, { count: 'exact' })
      .is('deleted_at', null); // Exclude soft-deleted records
    
    let query = startQuery
      .or('assessment_type.eq.behavioral,and(assessment_type.eq.work_behavior,assessment_metadata->>dual_assessment.eq.true)') // Include behavioral OR dual assessments
      .order('created_at', { ascending: false });

    // Apply company filtering
    if (activeCompanyId) {
      query = query.eq('company_id', activeCompanyId);
    } else if (!isSuperAdmin && companyId) {
      query = query.eq('company_id', companyId);
    }

    // Apply position filtering
    if (positionSubmissionIds !== null) {
      if (isUnassignedFilter) {
        // Show only submissions NOT in any position
        if (positionSubmissionIds.length > 0) {
          query = query.not('id', 'in', `(${positionSubmissionIds.join(',')})`);
        }
        // If no submissions are assigned, show all (no filter needed)
      } else {
        // Show only submissions in the specific position
        if (positionSubmissionIds.length > 0) {
          query = query.in('id', positionSubmissionIds);
        } else {
          // No members in this position - return empty result
          return { data: [], count: 0 };
        }
      }
    }

    // Apply search filter
    if (searchTerm) {
      query = query.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
    }

    // Apply filters
    if (filters) {
      // Trait range filters
      const traitKeys = ['dominance', 'extraversion', 'patience', 'formality', 'objectivity'] as const;
      for (const key of traitKeys) {
        const range = filters[key];
        if (range.min > 0 || range.max < 100) {
          query = query
            .gte(`scores->${key}`, range.min)
            .lte(`scores->${key}`, range.max);
        }
      }

      // Stage (status) filter
      if (filters.status && filters.status.length > 0) {
        if (filters.statusMode === 'exclude') {
          query = query.not('status', 'in', `(${filters.status.map(s => `"${s}"`).join(',')})`);
        } else {
          query = query.in('status', filters.status);
        }
      }
    }

    // Fetch more data than pageSize to account for duplicates that will be merged
    const startIndex = (page - 1) * pageSize;
    const fetchSize = pageSize * 3; // Fetch extra to ensure we get enough unique emails after deduplication
    const { data, error } = await query.range(startIndex, startIndex + fetchSize - 1);

    if (error) {
      console.error('Error fetching behavioral submissions:', error);
      throw error;
    }

    // UI-side deduplication: Group by email, keep newest, merge work behavior history
    const deduplicatedData = (data || []).reduce((acc: any[], submission) => {
      const existingIndex = acc.findIndex(s => s.email === submission.email);
      const scores = submission.scores as Record<string, any> | null;
      
      if (existingIndex === -1) {
        // First time seeing this email (newest due to DESC sort) - add to result
        acc.push({
          ...submission,
          scores: {
            ...(scores || {}),
            workBehaviorHistory: scores?.workBehaviorHistory || []
          }
        });
      } else {
        // Duplicate email (older submission) - merge work behavior into history
        const existing = acc[existingIndex];
        
        if (scores?.workBehavior) {
          // Add older work behavior to history array
          existing.scores.workBehaviorHistory = existing.scores.workBehaviorHistory || [];
          existing.scores.workBehaviorHistory.push({
            ...scores.workBehavior,
            completedAt: submission.completed_at
          });
        }
      }
      
      return acc;
    }, []);

    // Return only the requested pageSize of deduplicated results
    const paginatedData = deduplicatedData.slice(0, pageSize);

    return {
      data: paginatedData,
      count: deduplicatedData.length // Return unique count
    };
  }

  static async getWorkBehaviorSubmissions(
    page: number,
    pageSize: number,
    searchTerm?: string,
    filters?: FilterState,
    activeCompanyId?: string
  ): Promise<PaginatedData<any>> {
    console.log('AdminService.getWorkBehaviorSubmissions: Using company ID:', activeCompanyId);
    const { isSuperAdmin, companyId } = await this.getCurrentUserRole(activeCompanyId);

    let query = supabase
      .from('survey_submissions')
      .select('*', { count: 'exact' })
      .is('deleted_at', null) // Exclude soft-deleted records
      // Include both standalone work_behavior AND Full Assessments with workBehavior data
      .or('assessment_type.eq.work_behavior,scores->workBehavior.not.is.null')
      .order('created_at', { ascending: false });

    // Apply company filtering
    if (activeCompanyId) {
      query = query.eq('company_id', activeCompanyId);
    } else if (!isSuperAdmin && companyId) {
      query = query.eq('company_id', companyId);
    }

    // Apply search filter
    if (searchTerm) {
      query = query.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
    }

    // Apply filters
    if (filters) {
      // Trait range filters
      const traitKeys = ['dominance', 'extraversion', 'patience', 'formality', 'objectivity'] as const;
      for (const key of traitKeys) {
        const range = filters[key];
        if (range.min > 0 || range.max < 100) {
          query = query
            .gte(`scores->${key}`, range.min)
            .lte(`scores->${key}`, range.max);
        }
      }

      // Stage (status) filter
      if (filters.status && filters.status.length > 0) {
        if (filters.statusMode === 'exclude') {
          query = query.not('status', 'in', `(${filters.status.map(s => `"${s}"`).join(',')})`);
        } else {
          query = query.in('status', filters.status);
        }
      }
    }

    const startIndex = (page - 1) * pageSize;
    const { data, error, count } = await query.range(startIndex, startIndex + pageSize - 1);

    if (error) {
      console.error('Error fetching work behavior submissions:', error);
      throw error;
    }

    return {
      data: data || [],
      count: count || 0
    };
  }

  static async getIdealProfiles(page: number, pageSize: number, activeCompanyId?: string): Promise<PaginatedData<any>> {
    console.log('AdminService.getIdealProfiles: Using company ID:', activeCompanyId);
    const { isSuperAdmin, companyId } = await this.getCurrentUserRole(activeCompanyId);

    let query = supabase
      .from('ideal_profiles')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    // Apply company filtering - this is the key change for company-specific profile matching
    if (!isSuperAdmin && companyId) {
      query = query.eq('company_id', companyId);
    }

    const startIndex = (page - 1) * pageSize;
    const { data, error, count } = await query.range(startIndex, startIndex + pageSize - 1);

    if (error) {
      console.error('Error fetching ideal profiles:', error);
      throw error;
    }

    return {
      data: data || [],
      count: count || 0
    };
  }

  static async deleteIdealProfile(profileId: string): Promise<void> {
    const { error } = await supabase
      .from('ideal_profiles')
      .delete()
      .eq('id', profileId);

    if (error) {
      console.error('Error deleting ideal profile:', error);
      throw error;
    }
  }

  static async getCognitiveOnlySubmissions(activeCompanyId?: string): Promise<any[]> {
    try {
      const { isSuperAdmin, companyId } = await this.getCurrentUserRole(activeCompanyId);

      // First get all cognitive submissions
      let cognitiveQuery = supabase
        .from('cognitive_submissions')
        .select('*')
        .is('deleted_at', null) // Exclude soft-deleted records
        .order('created_at', { ascending: false });

      // Apply company filtering
      if (activeCompanyId) {
        cognitiveQuery = cognitiveQuery.eq('company_id', activeCompanyId);
      } else if (!isSuperAdmin && companyId) {
        cognitiveQuery = cognitiveQuery.eq('company_id', companyId);
      }

      const { data: cognitiveData, error: cogError } = await cognitiveQuery;
      
      if (cogError) {
        console.error('Error fetching cognitive submissions:', cogError);
        throw cogError;
      }

      if (!cognitiveData || cognitiveData.length === 0) {
        return [];
      }

      // Get all behavioral submission emails to exclude
      const { data: behavioralData, error: behavError } = await supabase
        .from('survey_submissions')
        .select('email')
        .is('deleted_at', null);

      if (behavError) {
        console.error('Error fetching behavioral emails:', behavError);
        throw behavError;
      }

      const behavioralEmails = new Set(behavioralData?.map(sub => sub.email) || []);

      // Filter cognitive submissions to only those without behavioral submissions
      const cognitiveOnly = cognitiveData.filter(cog => !behavioralEmails.has(cog.email));

      // Deduplicate by email, keeping the most recent
      const latestByEmail = cognitiveOnly.reduce((acc, item) => {
        if (!acc[item.email] || new Date(item.created_at) > new Date(acc[item.email].created_at)) {
          acc[item.email] = item;
        }
        return acc;
      }, {} as Record<string, any>);

      return Object.values(latestByEmail);
    } catch (error) {
      console.error('Error in getCognitiveOnlySubmissions:', error);
      throw error;
    }
  }

  static getCandidateAssessments = getCandidateAssessments;
}
